import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { UploadCloud, FileText, X, Rocket, KeyRound, Users, Check, Plus, Trash2 } from "lucide-react";
import { MixedPartsBuilder, newMcqPart, newSaqPart, mixedKeyRows, partsPayload } from "@/components/MixedPartsBuilder";

export default function UploadHomework() {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState(null);
  const [targets, setTargets] = useState({});
  const [titleEdited, setTitleEdited] = useState(false);
  const [formatTouched, setFormatTouched] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [title, setTitle] = useState("");
  const [timed, setTimed] = useState(true);
  const [timeLimit, setTimeLimit] = useState("30");
  const [numQuestions, setNumQuestions] = useState("20");
  const [optionCount, setOptionCount] = useState("5");
  const [answerKey, setAnswerKey] = useState({});
  const [answerFormat, setAnswerFormat] = useState("mcq");
  const [saqBoxes, setSaqBoxes] = useState([{ label: "Response 1", marks: "10" }]);
  const [parts, setParts] = useState([newMcqPart(), newSaqPart()]);
  const [autoPublish, setAutoPublish] = useState(true);
  const [file, setFile] = useState(null);
  const [solutionFile, setSolutionFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState([]);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data));
    api.get("/tutor/students").then((r) => setStudents(r.data));
    api.get("/tutor/groups").then((r) => setGroups(r.data));
  }, []);

  const progKeys = Object.keys(targets);
  const metaOf = (p) => programs?.[p]?.sections.find((s) => s.key === targets[p]);
  const chosen = progKeys.map(metaOf).filter(Boolean);
  const allWritingSections = chosen.length > 0 && chosen.length === progKeys.length && chosen.every((m) => m.type === "writing");
  const isWriting = allWritingSections && answerFormat === "writing";
  const autoTitle = (metas) => metas.map((m) => m.name).filter((v, i, a) => a.indexOf(v) === i).join(" / ");
  const n = Math.max(0, Math.min(parseInt(numQuestions) || 0, 120));
  const letters = Array.from({ length: Math.max(2, Math.min(parseInt(optionCount) || 5, 8)) }, (_, i) => String.fromCharCode(65 + i));
  const isMixed = !isWriting && answerFormat === "mixed";
  const keyRows = isMixed ? mixedKeyRows(parts) : Array.from({ length: n }, (_, i) => ({ num: i + 1, letters }));

  const applyTargets = (next) => {
    setTargets(next);
    const metas = Object.keys(next).map((p) => programs?.[p]?.sections.find((s) => s.key === next[p])).filter(Boolean);
    const allW = metas.length > 0 && metas.length === Object.keys(next).length && metas.every((m) => m.type === "writing");
    const wasAllW = allWritingSections;
    if (allW && !wasAllW && !formatTouched) setAnswerFormat("writing");
    if (!allW && answerFormat === "writing") setAnswerFormat("mcq");
    if (metas.length && !titleEdited) setTitle(autoTitle(metas));
    if (metas[0]?.questions && metas[0].type !== "writing") setNumQuestions(String(metas[0].questions));
  };
  const toggleProgram = (p) => {
    const next = { ...targets };
    if (p in next) { delete next[p]; setSelected(selected.filter((id) => students.find((s) => s.id === id)?.program !== p)); }
    else next[p] = "";
    applyTargets(next);
  };
  const onSection = (p, s) => applyTargets({ ...targets, [p]: s });

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please attach the exam PDF.");
    if (!progKeys.length || progKeys.some((p) => !targets[p])) return toast.error("Tick at least one program and choose a section for each.");
    if (autoPublish && selected.length === 0) return toast.error("Select at least one student to distribute to, or turn off ‘Publish now’ to save as a draft.");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("program", progKeys[0]);
    fd.append("section", targets[progKeys[0]]);
    fd.append("targets", JSON.stringify(progKeys.map((p) => ({ program: p, section: targets[p] }))));
    fd.append("due_date", dueDate);
    fd.append("title", progKeys.length > 1 && !titleEdited ? "" : (title || autoTitle(chosen)));
    fd.append("mode", timed ? "timed" : "practice");
    fd.append("time_limit", timeLimit || "30");
    fd.append("num_questions", isWriting ? "1" : String(n));
    fd.append("option_count", optionCount);
    fd.append("answer_key", JSON.stringify(answerKey));
    fd.append("auto_publish", autoPublish ? "true" : "false");
    fd.append("assigned_to", JSON.stringify(selected));
    fd.append("answer_format", isWriting ? "writing" : answerFormat);
    fd.append("saq_boxes", answerFormat === "saq" ? String(saqBoxes.length) : "0");
    fd.append("saq_questions", answerFormat === "saq" ? JSON.stringify(saqBoxes.map((b) => ({ label: b.label, max_marks: b.marks }))) : "[]");
    fd.append("parts", isMixed ? JSON.stringify(partsPayload(parts)) : "[]");
    if (solutionFile) fd.append("solution_file", solutionFile);
    try {
      const { data } = await api.post("/homework/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      const made = data.created || [];
      toast.success(autoPublish ? `Mock exam distributed to ${selected.length} student(s)${made.length > 1 ? ` across ${made.length} programs` : ""}` : made.length > 1 ? `Saved ${made.length} drafts.` : "Saved as draft.");
      navigate(made.length > 1 ? "/tutor/homework" : `/tutor/homework/${data.id}/review`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  const answered = keyRows.filter((r) => answerKey[r.num]).length;
  const progStudents = students.filter((s) => progKeys.includes(s.program));
  const allSelected = progStudents.length > 0 && progStudents.every((s) => selected.includes(s.id));
  const progGroups = groups.filter((g) => progKeys.includes(g.program));
  const applyGroup = (g) => {
    const ids = g.student_ids.filter((id) => progStudents.some((s) => s.id === id));
    setSelected([...new Set([...selected.filter((id) => students.find((s) => s.id === id)?.program !== g.program), ...ids])]);
    toast.success(`Selected ${ids.length} student(s) from “${g.name}”`);
  };

  return (
    <div className="animate-fade-up max-w-3xl">
      <PageHeader title="Upload Mock Exam" subtitle="Distribute the exam PDF to students with an online answer sheet — no re-typing of questions." />
      <form onSubmit={submit} className="space-y-6">
        <div className="ember-card p-6">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2" data-testid="program-targets">
              <Label>Programs &amp; sections</Label>
              <p className="mt-0.5 text-xs text-[#948EAB]">Tick every program this exam is for, then pick its section. Each program gets its own copy.</p>
              <div className="mt-2 space-y-2">
                {programs && Object.values(programs).map((p) => {
                  const on = p.key in targets;
                  return (
                    <div key={p.key} className={`flex flex-col gap-2 rounded-xl border px-3 py-2 sm:flex-row sm:items-center ${on ? "border-[#C4B5FD] bg-[#FAF9FE]" : "border-[#EEEAF8]"}`}>
                      <button type="button" onClick={() => toggleProgram(p.key)} data-testid={`program-check-${p.key}`} className="flex flex-1 items-center gap-2.5 text-left">
                        <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-md border-2 ${on ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-[#DDD6FE] text-transparent"}`}><Check className="h-3.5 w-3.5" /></span>
                        <span className="text-sm font-bold text-[#1E1B2E]">{p.name}</span>
                      </button>
                      {on && (
                        <Select value={targets[p.key]} onValueChange={(s) => onSection(p.key, s)}>
                          <SelectTrigger data-testid={`select-section-${p.key}`} className="h-10 rounded-xl sm:w-72"><SelectValue placeholder="Choose section" /></SelectTrigger>
                          <SelectContent>{p.sections.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Due date <span className="font-normal text-[#948EAB]">(optional)</span></Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="input-due-date" className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-[#EEEAF8] px-4">
              <div><Label className="cursor-pointer">Timed mode</Label><p className="text-xs text-[#948EAB]">{timed ? "Countdown + auto-submit" : "Practice, no timer"}</p></div>
              <Switch checked={timed} onCheckedChange={setTimed} data-testid="toggle-timed" />
            </div>
            <div className="sm:col-span-2">
              <Label>Exam Title</Label>
              <Input data-testid="input-title" value={title} onChange={(e) => { setTitle(e.target.value); setTitleEdited(true); }} placeholder="e.g. Numerical Reasoning Mock 1" className="mt-1.5 h-11 rounded-xl" />
              {progKeys.length > 1 && !titleEdited && <p className="mt-1 text-xs text-[#948EAB]">Each program's copy is named after its own section. Type a title to use the same name for all.</p>}
            </div>
            {timed && <div><Label>Time limit (minutes)</Label><Input data-testid="input-timelimit" type="number" value={timeLimit} onChange={(e) => setTimeLimit(e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>}
            {!isWriting && !isMixed && <>
              <div><Label>Number of questions</Label><Input data-testid="input-numq" type="number" min="1" max="120" value={numQuestions} onChange={(e) => setNumQuestions(e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
              <div>
                <Label>Options per question</Label>
                <Select value={optionCount} onValueChange={setOptionCount}>
                  <SelectTrigger data-testid="select-options" className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{[3, 4, 5, 6].map((c) => <SelectItem key={c} value={String(c)}>{c} (A–{String.fromCharCode(64 + c)})</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </>}
          </div>
        </div>

        <div className="ember-card p-6">
          <Label className="mb-2 block">Exam PDF</Label>
          {!file ? (
            <label onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); }}
              data-testid="dropzone"
              className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-12 text-center transition-colors ${drag ? "border-[#7C3AED] bg-[#F5F3FF]" : "border-[#DDD6FE] hover:bg-[#FAF9FE]"}`}>
              <span className="grid h-14 w-14 place-items-center rounded-2xl ember-gradient-soft text-[#7C3AED]"><UploadCloud className="h-7 w-7" /></span>
              <p className="mt-4 font-heading font-bold text-[#1E1B2E]">Drop the exam PDF here</p>
              <p className="mt-1 text-sm text-[#948EAB]">Students will read this exact PDF and answer on-screen · PDF, DOCX or image</p>
              <input type="file" accept=".pdf,.docx,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="file-input" />
            </label>
          ) : (
            <div className="flex items-center gap-3 rounded-2xl border border-[#DDD6FE] bg-[#FAF9FE] p-4">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#EDE9FE] text-[#5B21B6]"><FileText className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1"><div className="truncate font-bold text-[#1E1B2E]">{file.name}</div><div className="text-xs text-[#948EAB]">{(file.size / 1024).toFixed(0)} KB</div></div>
              <button type="button" onClick={() => setFile(null)} className="rounded-lg p-2 text-[#948EAB] hover:bg-white"><X className="h-4 w-4" /></button>
            </div>
          )}
        </div>

        <div className="ember-card p-6" data-testid="solutions-upload-card">
          <Label className="mb-1 block">Solutions / Model Answers PDF <span className="font-normal text-[#948EAB]">(optional)</span></Label>
          <p className="mb-2 text-xs text-[#948EAB]">Shown to students as soon as they submit, so they can compare their work to model answers straight away.</p>
          {!solutionFile ? (
            <label className="flex cursor-pointer items-center gap-3 rounded-2xl border-2 border-dashed border-[#FDE68A] bg-[#FFFBEB] px-5 py-6 transition-colors hover:bg-[#FEF9C3]/40" data-testid="solutions-dropzone">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-amber-100 text-amber-600"><FileText className="h-5 w-5" /></span>
              <div><p className="font-heading font-bold text-[#1E1B2E]">Add a solutions PDF</p><p className="text-xs text-[#948EAB]">PDF, DOCX or image · optional</p></div>
              <input type="file" accept=".pdf,.docx,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => setSolutionFile(e.target.files[0])} data-testid="tutor-upload-solutions-pdf-input" />
            </label>
          ) : (
            <div className="flex items-center gap-3 rounded-2xl border border-[#FDE68A] bg-[#FFFBEB] p-4">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-amber-100 text-amber-600"><FileText className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1"><div className="truncate font-bold text-[#1E1B2E]">{solutionFile.name}</div><div className="text-xs text-[#948EAB]">{(solutionFile.size / 1024).toFixed(0)} KB</div></div>
              <button type="button" onClick={() => setSolutionFile(null)} className="rounded-lg p-2 text-[#948EAB] hover:bg-white"><X className="h-4 w-4" /></button>
            </div>
          )}
        </div>

        {!isWriting && keyRows.length > 0 && answerFormat !== "saq" && (
          <div className="ember-card p-6">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2"><span className="grid h-8 w-8 place-items-center rounded-lg bg-[#EDE9FE] text-[#5B21B6]"><KeyRound className="h-4 w-4" /></span><Label className="text-[#1E1B2E]">Answer key <span className="font-normal text-[#948EAB]">(for auto-marking — optional)</span></Label></div>
              <span className="text-xs font-semibold text-[#7C3AED]">{answered}/{keyRows.length} set</span>
            </div>
            <div className="grid max-h-72 grid-cols-1 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
              {keyRows.map(({ num: q, letters }) => (
                <div key={q} className="flex items-center gap-2 rounded-xl border border-[#EEEAF8] px-3 py-1.5" data-testid={`ak-row-${q}`}>
                  <span className="w-8 shrink-0 text-sm font-bold text-[#1E1B2E]">Q{q}</span>
                  <div className="flex flex-wrap gap-1">
                    {letters.map((l) => {
                      const sel = answerKey[q] === l;
                      return (
                        <button type="button" key={l} data-testid={`ak-${q}-${l}`}
                          onClick={() => setAnswerKey({ ...answerKey, [q]: sel ? "" : l })}
                          className={`grid h-7 w-7 place-items-center rounded-lg border text-xs font-bold transition-colors ${sel ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-[#E9E5F5] text-[#7C3AED] hover:border-[#C4B5FD]"}`}>{l}</button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {chosen.length > 0 && (
          <div className="ember-card p-5" data-testid="format-toggle">
            <Label className="text-[#1E1B2E]">Answer format</Label>
            <div className="mt-2 flex flex-wrap gap-1 rounded-xl bg-[#F5F3FF] p-1 sm:inline-flex">
              {[...(allWritingSections ? [["writing", "Writing task (essay + feedback)"]] : []), ["mcq", "Multiple choice"], ["saq", "Written / short answers"], ["mixed", "Mixed (MCQ + short answer)"]].map(([v, l]) => (
                <button type="button" key={v} data-testid={`fmt-${v}`} onClick={() => { setAnswerFormat(v); setFormatTouched(true); }}
                  className={`rounded-lg px-4 py-1.5 text-sm font-bold transition-colors ${answerFormat === v ? "bg-white text-[#5B21B6] shadow-sm" : "text-[#948EAB]"}`}>{l}</button>
              ))}
            </div>
            {isWriting && <p className="mt-3 text-xs text-[#948EAB]" data-testid="writing-format-note">Students type an essay beside the PDF and you give highlighted feedback and a /40 score.</p>}
            {isMixed && <MixedPartsBuilder parts={parts} setParts={setParts} />}
            {answerFormat === "saq" && (
              <div className="mt-3 space-y-2" data-testid="saq-builder">
                <p className="text-xs text-[#948EAB]">Add a box for each written answer. Set the marks available per box — you'll mark them by hand after students submit.</p>
                {saqBoxes.map((b, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`saq-box-${i}`}>
                    <Input
                      data-testid={`saq-box-label-${i}`}
                      value={b.label}
                      onChange={(e) => setSaqBoxes(saqBoxes.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                      placeholder={`Response ${i + 1}`}
                      className="h-10 flex-1 rounded-xl"
                    />
                    <div className="flex items-center gap-1">
                      <Input
                        data-testid={`saq-box-marks-${i}`}
                        type="number"
                        min="1"
                        max="100"
                        value={b.marks}
                        onChange={(e) => setSaqBoxes(saqBoxes.map((x, j) => (j === i ? { ...x, marks: e.target.value } : x)))}
                        className="h-10 w-20 rounded-xl"
                      />
                      <span className="text-xs text-[#948EAB]">marks</span>
                    </div>
                    <button
                      type="button"
                      data-testid={`saq-box-remove-${i}`}
                      onClick={() => setSaqBoxes(saqBoxes.filter((_, j) => j !== i))}
                      disabled={saqBoxes.length <= 1}
                      className="grid h-10 w-10 place-items-center rounded-xl text-[#948EAB] hover:bg-[#F5F3FF] hover:text-[#5B21B6] disabled:opacity-30"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  data-testid="saq-add-box"
                  onClick={() => setSaqBoxes([...saqBoxes, { label: `Response ${saqBoxes.length + 1}`, marks: "10" }])}
                  className="rounded-xl"
                >
                  <Plus className="mr-1 h-4 w-4" /> Add answer box
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="ember-card p-6" data-testid="distribute-card">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#EDE9FE] text-[#5B21B6]"><Users className="h-4 w-4" /></span>
              <Label className="text-[#1E1B2E]">Distribute to students {progKeys.length > 0 && <span className="font-normal text-[#948EAB]">({progStudents.length} in {progKeys.map((p) => programs?.[p]?.short || programs?.[p]?.name).join(", ")})</span>}</Label>
            </div>
            <button type="button" data-testid="select-all-students" onClick={() => setSelected(allSelected ? [] : progStudents.map((s) => s.id))} className="text-xs font-semibold text-[#7C3AED] hover:underline">{allSelected ? "Clear all" : "Select all"}</button>
          </div>
          {!progKeys.length ? (
            <p className="text-sm text-[#948EAB]">Tick a program above to see its students.</p>
          ) : progStudents.length === 0 ? (
            <p className="text-sm text-[#948EAB]">No students in these programs yet.</p>
          ) : (
            <>
              {progGroups.length > 0 && (
                <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="group-chips">
                  <span className="text-xs font-semibold text-[#948EAB]">Quick select:</span>
                  {progGroups.map((g) => (
                    <button type="button" key={g.id} data-testid={`group-chip-${g.id}`} onClick={() => applyGroup(g)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#DDD6FE] bg-[#F5F3FF] px-3 py-1 text-xs font-bold text-[#5B21B6] transition-colors hover:bg-[#EDE9FE]">
                      <Users className="h-3 w-3" /> {g.name} <span className="text-[#948EAB]">({g.student_ids.length})</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="grid max-h-64 grid-cols-1 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
              {progStudents.map((s) => {
                const on = selected.includes(s.id);
                return (
                  <button type="button" key={s.id} data-testid={`student-pick-${s.id}`}
                    onClick={() => setSelected(on ? selected.filter((x) => x !== s.id) : [...selected, s.id])}
                    className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors ${on ? "border-[#7C3AED] bg-[#FAF9FE]" : "border-[#EEEAF8] hover:border-[#C4B5FD]"}`}>
                    <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-md border-2 ${on ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-[#DDD6FE] text-transparent"}`}><Check className="h-3.5 w-3.5" /></span>
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#1E1B2E]">{s.name}</span>
                    {progKeys.length > 1 && <span className="shrink-0 text-[10px] font-bold uppercase text-[#948EAB]">{programs?.[s.program]?.short}</span>}
                  </button>
                );
              })}
            </div>
            </>
          )}
          <p className="mt-3 text-xs text-[#948EAB]">{selected.length} student{selected.length === 1 ? "" : "s"} selected · only selected students will receive this exam.</p>
        </div>

        <div className="ember-card flex items-center gap-4 p-5">
          <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${autoPublish ? "ember-gradient text-white" : "bg-[#F5F3FF] text-[#7C3AED]"}`}><Rocket className="h-5 w-5" /></span>
          <div className="flex-1"><Label className="cursor-pointer text-[#1E1B2E]">Publish to students now</Label><p className="text-xs text-[#948EAB]">{autoPublish ? "Students can start this mock exam immediately." : "Save as a draft to release later."}</p></div>
          <Switch checked={autoPublish} onCheckedChange={setAutoPublish} data-testid="toggle-autopublish" />
        </div>

        <Button type="submit" disabled={busy} data-testid="upload-submit" className="h-12 w-full rounded-xl ember-gradient text-base font-bold shadow-lg shadow-violet-500/30">
          {busy ? "Uploading…" : autoPublish ? "Publish Mock Exam" : "Save as Draft"}
        </Button>
      </form>
    </div>
  );
}
